//***********************************************************
// Angular
//***********************************************************
import { NgModule, APP_INITIALIZER, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

//***********************************************************
// MJ
//***********************************************************
import { MJExplorerModulesBundle, SharedService } from '@memberjunction/ng-explorer-modules';
import { AuthServicesModule, RedirectComponent, MJAuthBase } from '@memberjunction/ng-auth-services';
import { MJExplorerAppModule } from '@memberjunction/ng-explorer-app';

//***********************************************************
// Auth0
//***********************************************************
import { AuthModule } from '@auth0/auth0-angular';

// Force Auth0 provider registration - tree-shaking removes the @RegisterClass decorator otherwise
import { MJAuth0Provider } from '@memberjunction/ng-auth-services/dist/lib/providers/mjexplorer-auth0-provider.service';
// Reference to prevent tree-shaking
const _forceAuth0Registration = MJAuth0Provider;

// Import pre-built MJ class registrations manifest (covers all @memberjunction/* packages)
import { CLASS_REGISTRATIONS } from '@memberjunction/ng-bootstrap';
// Import supplemental manifest for non-MJ classes (BCSaaS, BAC, Izzy)
// Generated at prestart/prebuild via `mj codegen manifest --exclude-packages @memberjunction`
import { CLASS_REGISTRATIONS as LOCAL_CLASSES } from './generated/class-registrations-manifest';
// Static code path so bundler cannot tree-shake it
const _registrations = [...CLASS_REGISTRATIONS, ...LOCAL_CLASSES];

//***********************************************************
// Project stuff
//***********************************************************
// Stop boot-loading ArtifactVersion.Content (244MB on prod → 12s boots +
// every-boot cache wipe). Side-effect import, must run before login.
import './patches/artifact-boot-load-trim';
import { AppComponent } from './app.component';
import { GeneratedFormsModule } from './generated/generated-forms.module';
import { environment } from 'src/environments/environment';
import { NavigationItemDemoComponent } from './demo/navigation-item.component';

//***********************************************************
// Izzy UI
//***********************************************************
import { IzzyUIModule } from 'izzy-ui';

/**
 * Initialize auth provider before Angular routing starts.
 * This ensures Auth0 can process OAuth redirect responses before Angular's router
 * consumes the URL hash.
 */
export function initializeAuth(authService: MJAuthBase): () => Promise<void> {
  return () => authService.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    NavigationItemDemoComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,

    // MJ Consolidated Bundle (includes all MJ + Kendo modules)
    MJExplorerModulesBundle,

    // Auth0 Angular SDK — provides auth0.client token directly (required by AuthService)
    AuthModule.forRoot({
      domain: environment.AUTH0_DOMAIN,
      clientId: environment.AUTH0_CLIENTID,
      authorizationParams: {
        redirect_uri: window.location.origin,
        scope: 'openid profile email offline_access',
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      skipRedirectCallback: window.location.pathname.startsWith('/oauth/callback'),
    }),

    // Auth (needs forRoot configuration)
    AuthServicesModule.forRoot(environment),

    // Explorer App Shell (includes login UI, validation, and mj-shell wrapper)
    MJExplorerAppModule.forRoot(environment),

    // App-specific modules
    GeneratedFormsModule,
    IzzyUIModule,
  ],
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    SharedService,
    provideHttpClient(withInterceptorsFromDi()),
    // Explicit fallback: guarantee MJAuthBase resolves to MJAuth0Provider regardless of ClassFactory timing
    { provide: MJAuthBase, useClass: MJAuth0Provider },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [MJAuthBase],
      multi: true
    }
  ],
  bootstrap: [AppComponent, RedirectComponent],
})
export class AppModule {}
